import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Delivery, Files } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft, Printer, Eye, Download, AlertTriangle, CheckCircle2, Clock,
  XCircle, Truck, ShieldCheck, Hash, FileText, FileBox, Image as ImageIcon, X,
  ClipboardList, BadgeCheck,
} from "lucide-react";
import { cn } from "../lib/utils";
import { isImage, isPdf } from "../lib/deliveryFile";
import { formatAuditDate, formatAuditTime, fmtDate } from "../lib/auditFormat";

/**
 * Delivery File / Delivery Kit Checklist — read-only verification screen.
 *
 * 14-row structured checklist driven by /api/sales/:id/delivery_checklist.
 * No uploads on this screen — every status is auto-synced from the source
 * module (Sales / Accounts / Insurance / Road Tax / VAS / Spares / PDI).
 *
 * Visible to: DSE (own customers only), Accounts, Back Office, Admin.
 */

const STATUS_TONE = {
  available:        { label: "Available",        bg: "bg-emerald-100", text: "text-emerald-700", ring: "ring-emerald-200", icon: CheckCircle2 },
  pending:          { label: "Pending",          bg: "bg-amber-100",   text: "text-amber-700",   ring: "ring-amber-200",   icon: Clock         },
  not_applicable:   { label: "Not Applicable",   bg: "bg-slate-100",   text: "text-slate-600",   ring: "ring-slate-200",   icon: XCircle       },
  pre_printed:      { label: "Pre Printed",      bg: "bg-sky-100",     text: "text-sky-700",     ring: "ring-sky-200",     icon: BadgeCheck    },
  company_provided: { label: "Company Provided", bg: "bg-sky-100",     text: "text-sky-700",     ring: "ring-sky-200",     icon: BadgeCheck    },
};

function StatusPill({ status, testid }) {
  const t = STATUS_TONE[status] || STATUS_TONE.pending;
  const Icon = t.icon;
  return (
    <span className={cn(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
      t.bg, t.text, t.ring,
    )} data-testid={testid}>
      <Icon className="h-3 w-3" /> {t.label}
    </span>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return `${formatAuditDate(iso)} · ${formatAuditTime(iso)}`;
}

export default function DeliveryFileDetail() {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const { activeUser, locations } = useApp();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [missingOpen, setMissingOpen] = useState(false);

  const load = useCallback(async () => {
    const d = await Delivery.checklist(saleId);
    setData(d);
  }, [saleId]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="py-12 text-center text-sm text-slate-500">Loading delivery file…</div>;
  }
  const { sale, checklist, summary, missing_mandatory } = data;
  const loc = locations.find((l) => l.id === sale.location_id);

  const hasAccess = (
    activeUser?.role === "Admin" ||
    activeUser?.role === "Accounts" ||
    activeUser?.role === "BackOffice" ||
    (activeUser?.role === "DSE" && sale.dse_id === activeUser?.id)
  );
  if (!hasAccess) {
    return (
      <div className="py-12 text-center text-sm text-slate-500" data-testid="df-no-access">
        You don’t have access to this customer’s delivery file.
      </div>
    );
  }

  const goPrint = () => navigate(`/delivery-files/${saleId}/print`);
  const onClickPrint = () => {
    if ((missing_mandatory || []).length > 0) {
      setMissingOpen(true); return;
    }
    goPrint();
  };

  const headerTone = summary.final_delivery_status === "delivered"
    ? "from-emerald-600 to-emerald-500"
    : summary.delivery_ready
    ? "from-sky-700 to-sky-600"
    : "from-slate-700 to-slate-600";

  return (
    <div className="space-y-4 pb-12" data-testid="delivery-file-detail-page">
      <button
        onClick={() => navigate("/delivery-files")}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
        data-testid="df-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Back to Delivery Files
      </button>

      {/* ─── Header ─── */}
      <div data-testid="df-header">
        <Card className={cn(
          "overflow-hidden border-0 bg-gradient-to-r p-5 text-white shadow-md",
          headerTone,
        )}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-white/20 px-2 py-0.5 font-mono-data text-xs font-semibold">
                  {sale.sr_number || "—"}
                </span>
                <span className="text-xs font-semibold uppercase tracking-wider opacity-90">
                  Delivery File · Kit Checklist
                </span>
                <DeliveryStatusPill status={summary.final_delivery_status} />
              </div>
              <div className="mt-1 font-display text-xl font-semibold sm:text-2xl">
                {sale.customer?.name || "—"}
                <span className="ml-2 text-base font-normal opacity-90">
                  · {sale.vehicle?.model || "—"}
                </span>
              </div>
              <div className="mt-0.5 text-xs opacity-90">
                {loc?.name} ({loc?.code})
              </div>
            </div>
            <Button
              onClick={onClickPrint}
              className="bg-white text-slate-900 hover:bg-slate-100"
              data-testid="df-print-btn"
            >
              <Printer className="mr-1.5 h-4 w-4" /> Print Delivery File
            </Button>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <HeaderField label="VIN" value={sale.vehicle?.vin} mono />
            <HeaderField label="Registration No." value={sale.registration?.regn_no || sale.road_tax?.regn_no} mono />
            <HeaderField label="Delivery Date" value={fmtDate(sale.delivery?.promised_date) || "—"} />
            <HeaderField label="Delivery Time" value={sale.delivery?.promised_time || "—"} />
            <HeaderField label="Billing Ledger" value={sale.accounts?.billing_ledger_name || "—"} cols={2} />
            <HeaderField label="Current Status" value={(sale.status || "—").replace(/_/g, " ")} cols={2} />
          </div>
        </Card>
      </div>

      {/* ─── Top Status Section ─── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="df-status-cards">
        <StatusCard
          label="Delivery Ready"
          value={summary.delivery_ready ? "Ready" : "Not Ready"}
          icon={Truck}
          ok={summary.delivery_ready}
          testid="df-stat-delivery-ready"
        />
        <StatusCard
          label="PDI Ready"
          value={summary.pdi_ready ? "Ready" : "Pending"}
          icon={ShieldCheck}
          ok={summary.pdi_ready}
          testid="df-stat-pdi-ready"
        />
        <StatusCard
          label="Documents Pending"
          value={summary.documents_pending}
          icon={ClipboardList}
          ok={summary.documents_pending === 0}
          testid="df-stat-docs-pending"
        />
        <StatusCard
          label="Final Delivery Status"
          value={(summary.final_delivery_status || "pending").replace(/_/g, " ")}
          icon={BadgeCheck}
          ok={summary.final_delivery_status === "delivered" || summary.final_delivery_status === "ready"}
          testid="df-stat-final"
        />
      </div>

      {/* ─── Checklist Table ─── */}
      <Card className="border-slate-200 p-0 overflow-hidden" data-testid="df-checklist-card">
        <div className="border-b border-slate-200 px-5 py-3">
          <h2 className="font-display text-base font-semibold text-slate-900">
            Delivery File Checklist
          </h2>
          <p className="text-xs text-slate-500">
            Documents auto-sync from source modules — upload from the respective module screen.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="df-checklist-table">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Sr #</th>
                <th className="px-3 py-2 text-left">Document Name</th>
                <th className="px-3 py-2 text-left">Source Module</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Remarks</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {checklist.map((row) => (
                <ChecklistRow
                  key={`${row.sr}-${row.title}`}
                  row={row}
                  onPreview={setPreview}
                  onGallery={setGallery}
                />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ─── Missing-Mandatory Warning Dialog ─── */}
      <Dialog open={missingOpen} onOpenChange={setMissingOpen}>
        <DialogContent data-testid="df-missing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Some mandatory delivery documents are pending.
            </DialogTitle>
            <DialogDescription>
              Review the missing items below before proceeding to print.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            The following items are required for customer handover:
          </p>
          <ul className="list-disc pl-6 text-sm text-slate-800" data-testid="df-missing-list">
            {(missing_mandatory || []).map((m) => (
              <li key={m.sr}>{m.title}</li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissingOpen(false)}
                    data-testid="df-missing-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700"
                    onClick={() => { setMissingOpen(false); goPrint(); }}
                    data-testid="df-missing-continue">Continue Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Preview Dialog ─── */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3">
              <span className="truncate">{preview?.original_filename || "Preview"}</span>
              <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700">
                <X className="h-4 w-4" />
              </button>
            </DialogTitle>
          </DialogHeader>
          {preview && <FilePreview file={preview} />}
        </DialogContent>
      </Dialog>

      {/* ─── Gallery Dialog (Vehicle Photos) ─── */}
      <Dialog open={!!gallery} onOpenChange={(o) => !o && setGallery(null)}>
        <DialogContent className="max-w-4xl" data-testid="df-photo-gallery">
          <DialogHeader>
            <DialogTitle>Vehicle Photos · {gallery?.length || 0}</DialogTitle>
            <DialogDescription>
              Preview of all vehicle photos uploaded from the PDI module.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {(gallery || []).map((p) => (
              <a key={p.id} href={Files.url(p.id)} target="_blank" rel="noreferrer"
                 className="group relative block overflow-hidden rounded-md border border-slate-200">
                <img src={Files.url(p.id)} alt={p.original_filename}
                     className="h-40 w-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-2 py-1 text-[11px] text-white">
                  {p.original_filename}
                </div>
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────── Pieces ───────────

function HeaderField({ label, value, mono, cols }) {
  return (
    <div className={cn("rounded-md bg-white/15 px-3 py-2", cols === 2 && "sm:col-span-2")}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-90">{label}</div>
      <div className={cn("mt-0.5 truncate text-sm font-semibold capitalize", mono && "font-mono-data normal-case")}>
        {value || "—"}
      </div>
    </div>
  );
}

function DeliveryStatusPill({ status }) {
  const map = {
    ready:     { label: "Ready for Delivery",   cls: "bg-emerald-100/30 ring-emerald-200/40" },
    pending:   { label: "Pending Documents",    cls: "bg-amber-100/30 ring-amber-200/40" },
    delivered: { label: "Delivered",            cls: "bg-emerald-100/30 ring-emerald-200/40" },
  };
  const m = map[status] || map.pending;
  return (
    <span className={cn(
      "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold text-white ring-1",
      m.cls,
    )} data-testid="df-final-pill">
      {m.label}
    </span>
  );
}

function StatusCard({ label, value, icon: Icon, ok, testid }) {
  return (
    <Card className={cn(
      "p-4 border",
      ok ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40",
    )} data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className={cn(
          "flex h-8 w-8 items-center justify-center rounded-md",
          ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700",
        )}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      </div>
      <div className="mt-2 font-display text-xl font-semibold capitalize text-slate-900">
        {value}
      </div>
    </Card>
  );
}

function ChecklistRow({ row, onPreview, onGallery }) {
  const file = row.file;
  const photos = row.photos || [];
  const meta = row.meta || {};
  const isPhotoRow = row.title === "Vehicle Photos";
  const isPdiRow = row.title === "PDI Readiness";
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`df-row-${row.sr}`}>
      <td className="px-3 py-3 align-top font-mono-data text-xs text-slate-500">{row.sr}</td>
      <td className="px-3 py-3 align-top">
        <div className="font-semibold text-slate-900">{row.title}</div>
        {file && (
          <div className="mt-0.5 text-[11px] text-slate-500">
            Uploaded {formatAuditDate(file.uploaded_at)} · {formatAuditTime(file.uploaded_at)}
            {file.uploaded_by_name && <> · by <span className="text-slate-700">{file.uploaded_by_name}</span></>}
          </div>
        )}
        {isPdiRow && meta.completed_at && (
          <div className="mt-0.5 text-[11px] text-slate-500">
            Completed {fmtDateTime(meta.completed_at)}
            {meta.completed_by_name && <> · by <span className="text-slate-700">{meta.completed_by_name}</span></>}
          </div>
        )}
        {isPhotoRow && photos.length > 0 && (
          <div className="mt-0.5 text-[11px] text-slate-500">
            {photos.length} photo{photos.length === 1 ? "" : "s"} uploaded
          </div>
        )}
      </td>
      <td className="px-3 py-3 align-top text-xs text-slate-600">{row.source}</td>
      <td className="px-3 py-3 align-top">
        <StatusPill status={row.status} testid={`df-status-${row.sr}`} />
      </td>
      <td className="px-3 py-3 align-top text-xs text-slate-600">{row.remarks}</td>
      <td className="px-3 py-3 align-top text-right">
        {file && (
          <div className="inline-flex gap-1.5">
            <button
              onClick={() => onPreview(file)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              data-testid={`df-preview-${row.sr}`}
            >
              <Eye className="h-3 w-3" /> Preview
            </button>
            <a
              href={Files.url(file.id)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              data-testid={`df-download-${row.sr}`}
            >
              <Download className="h-3 w-3" /> Download
            </a>
          </div>
        )}
        {isPhotoRow && photos.length > 0 && (
          <div className="inline-flex gap-1.5">
            <button
              onClick={() => onGallery(photos)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              data-testid={`df-gallery-${row.sr}`}
            >
              <ImageIcon className="h-3 w-3" /> Preview Gallery
            </button>
          </div>
        )}
        {!file && !(isPhotoRow && photos.length > 0) && (
          <span className="text-xs italic text-slate-400">—</span>
        )}
      </td>
    </tr>
  );
}

function FilePreview({ file }) {
  const url = Files.url(file.id);
  if (isImage(file.content_type)) {
    return <img src={url} alt={file.original_filename} className="mx-auto max-h-[70vh] rounded" />;
  }
  if (isPdf(file.content_type)) {
    return <iframe src={url} title={file.original_filename} className="h-[70vh] w-full rounded border" />;
  }
  return (
    <div className="rounded-md bg-slate-50 p-6 text-center text-sm text-slate-600">
      Preview not available for this file type.
    </div>
  );
}
